import { type ReactNode, useEffect, useState } from 'react';
import { FilterBar } from '@/components/ui';
import { cn } from '@/lib/utils';
import { type IMetricViewTimePill, METRIC_VIEW_CUSTOM_PILL_ID, isValidIsoDate } from '@/lib/metric-view/date-presets';
import type { MetricViewIssueType } from '@/hooks/use-metric-view-filters';
import type { IFilterProduct, IFilterTeam } from '@skystone/shared';

interface IMetricViewFiltersProps {
  productId: string;
  teamId: string;
  pillId: string;
  issueType: MetricViewIssueType;
  /** PRODOPSAI-621: include archived teams in all data queries. Default off. */
  includeArchived: boolean;
  /** PRODOPSAI-648: true when a custom from/to range is active. */
  isCustom: boolean;
  /** Current resolved range (ISO `YYYY-MM-DD`); drives the date inputs. */
  from: string;
  to: string;
  products: IFilterProduct[];
  teams: IFilterTeam[];
  pills: ReadonlyArray<IMetricViewTimePill>;
  onProductChange: (value: string) => void;
  onTeamChange: (value: string) => void;
  onPillChange: (pillId: string) => void;
  /** PRODOPSAI-648: fired when either custom date input changes. */
  onCustomRangeChange: (from: string, to: string) => void;
  onIssueTypeChange: (type: MetricViewIssueType) => void;
  onIncludeArchivedChange: (next: boolean) => void;
  /**
   * Greys out the Type row (Story/Bug/Task pills) when true. The row stays
   * rendered so the filter bar's height does not jump between tabs (epic D6,
   * revised). DORA + Code Quality tabs pass true since the type filter
   * doesn't apply there; Product Flow leaves it false.
   */
  issueTypeRowDisabled?: boolean;
}

const TYPE_OPTIONS: ReadonlyArray<{ id: MetricViewIssueType; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'epic', label: 'Epic' },
  { id: 'story', label: 'Story' },
  { id: 'bug', label: 'Bug' },
  { id: 'task', label: 'Task' },
];

function PillButton({
  active,
  onClick,
  children,
  disabled = false,
}: Readonly<{ active: boolean; onClick: () => void; children: ReactNode; disabled?: boolean }>) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'rounded-md border px-3 py-1 text-sm transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-background text-foreground hover:bg-muted',
        disabled && 'cursor-not-allowed opacity-50 hover:bg-background',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Commit a custom range to the URL only when both sides form a valid, ordered
 * range. Defined at module scope (not inside the component) to satisfy
 * SonarCloud S7721. While one input is empty or the pair is reversed, nothing
 * is written — the local draft keeps the in-progress edit on screen and the
 * page stays in custom mode (the prior valid range remains the active filter).
 */
function commitCustomRange(
  from: string,
  to: string,
  commit: (from: string, to: string) => void,
): void {
  if (isValidIsoDate(from) && isValidIsoDate(to) && from <= to) {
    commit(from, to);
  }
}

function RowLabel({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <span className="w-16 shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </span>
  );
}

export function MetricViewFilters({
  productId,
  teamId,
  pillId,
  issueType,
  includeArchived,
  isCustom,
  from,
  to,
  products,
  teams,
  pills,
  onProductChange,
  onTeamChange,
  onPillChange,
  onCustomRangeChange,
  onIssueTypeChange,
  onIncludeArchivedChange,
  issueTypeRowDisabled = false,
}: Readonly<IMetricViewFiltersProps>) {
  const scopeSelectBase = 'rounded-md border border-border bg-background px-3 py-1.5 text-sm';
  const dateInputBase = 'rounded-md border border-border bg-background px-2 py-1 text-sm';

  // Local draft for the custom date inputs. The inputs edit this freely —
  // including clearing a field — and only commit to the URL via
  // `commitCustomRange` once both sides form a valid ordered range. This keeps
  // the fields editable (no snap-back) and stops an in-progress edit from
  // collapsing custom mode. Resynced whenever the resolved range changes
  // (e.g. clicking the Custom pill seeds it from the current window).
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  useEffect(() => { setDraftFrom(from); }, [from]);
  useEffect(() => { setDraftTo(to); }, [to]);

  return (
    <FilterBar className="space-y-3">
      <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:gap-3">
        <RowLabel>Scope</RowLabel>
        <select
          value={productId}
          onChange={(e) => onProductChange(e.target.value)}
          className={cn(scopeSelectBase, 'w-full md:w-auto')}
          aria-label="Filter by product"
        >
          <option value="">All Products</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>{p.productName}</option>
          ))}
        </select>
        <select
          value={teamId}
          onChange={(e) => onTeamChange(e.target.value)}
          className={cn(scopeSelectBase, 'w-full md:w-auto')}
          aria-label="Filter by team"
        >
          <option value="">All Teams</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>{t.teamName}</option>
          ))}
        </select>
        {/* PRODOPSAI-621: opt-in inclusion of archived teams. Mirrors the
            AdminTeamsPage "Show archived" checkbox pattern; default off. */}
        <label
          htmlFor="metric-view-include-archived"
          className="flex cursor-pointer select-none items-center gap-2 text-xs text-muted-foreground"
        >
          <input
            id="metric-view-include-archived"
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => onIncludeArchivedChange(e.target.checked)}
            className="h-4 w-4 cursor-pointer rounded border-input accent-primary"
            data-testid="metric-view-include-archived"
          />
          Include archived teams
        </label>
      </div>

      <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:gap-2">
        <RowLabel>Time</RowLabel>
        <select
          value={pillId}
          onChange={(e) => onPillChange(e.target.value)}
          className={cn(scopeSelectBase, 'w-full md:hidden')}
          aria-label="Filter by time period"
        >
          {pills.map((pill) => (
            <option key={pill.id} value={pill.id}>{pill.label}</option>
          ))}
          <option value={METRIC_VIEW_CUSTOM_PILL_ID}>Custom</option>
        </select>
        <div className="hidden flex-wrap items-center gap-2 md:flex">
          {pills.map((pill) => (
            <PillButton
              key={pill.id}
              active={pill.id === pillId}
              onClick={() => onPillChange(pill.id)}
            >
              {pill.label}
            </PillButton>
          ))}
          {/* PRODOPSAI-648: custom date-range entry point. */}
          <PillButton
            active={isCustom}
            onClick={() => onPillChange(METRIC_VIEW_CUSTOM_PILL_ID)}
          >
            Custom
          </PillButton>
        </div>
        {isCustom && (
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="metric-view-date-from" className="sr-only">From date</label>
            <input
              id="metric-view-date-from"
              type="date"
              value={draftFrom}
              max={draftTo || undefined}
              onChange={(e) => {
                setDraftFrom(e.target.value);
                commitCustomRange(e.target.value, draftTo, onCustomRangeChange);
              }}
              className={dateInputBase}
              aria-label="Custom range start date"
              data-testid="metric-view-date-from"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <label htmlFor="metric-view-date-to" className="sr-only">To date</label>
            <input
              id="metric-view-date-to"
              type="date"
              value={draftTo}
              min={draftFrom || undefined}
              onChange={(e) => {
                setDraftTo(e.target.value);
                commitCustomRange(draftFrom, e.target.value, onCustomRangeChange);
              }}
              className={dateInputBase}
              aria-label="Custom range end date"
              data-testid="metric-view-date-to"
            />
          </div>
        )}
      </div>

      <div
        className={cn(
          'flex flex-wrap items-center gap-2',
          issueTypeRowDisabled && 'pointer-events-none',
        )}
        aria-disabled={issueTypeRowDisabled || undefined}
      >
        <RowLabel>Type</RowLabel>
        {TYPE_OPTIONS.map((opt) => (
          <PillButton
            key={opt.id}
            active={opt.id === issueType}
            disabled={issueTypeRowDisabled}
            onClick={() => onIssueTypeChange(opt.id)}
          >
            {opt.label}
          </PillButton>
        ))}
        <span className="ml-auto hidden text-xs text-muted-foreground md:inline">
          {issueTypeRowDisabled
            ? 'Type filter not applicable to this tab.'
            : 'Click a type to narrow; click All to reset.'}
        </span>
      </div>
    </FilterBar>
  );
}
